import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Search, RefreshCw, Download, X, Calendar, User, Hash, Info } from 'lucide-react';
import { listReports, getReport, downloadReport } from '../services/ReportAPI';
import { getCurrentUserId } from '../services/apiConfig';
import ReportTable from './ReportTable';
import './ReportsPage.css';

const META_KEYS = ['id', 'training_data_id', 'created_at', 'updated_at', 'scan_user', 'video'];

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [notice, setNotice] = useState(null);
  const searchDebounceRef = useRef(null);

  const user_id = getCurrentUserId();

  const load = useCallback(async (query) => {
    if (!user_id) {
      setError('No user session found. Please log in again.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await listReports({ user_id, search: (query && query.trim()) || undefined, sort });
      setReports((data && data.data) || []);
    } catch (err) {
      setError(err.message || 'Failed to load reports.');
    } finally {
      setLoading(false);
    }
  }, [user_id, sort]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => load(search), 400);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [search, load]);

  const handleRefresh = () => load(search);

  const handleView = async (report) => {
    setSelected(report);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const data = await getReport(report.id, user_id);
      setDetail((data && data.data) || null);
    } catch (err) {
      setDetailError(err.message || 'Failed to load report details.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDownload = async (report) => {
    setNotice(null);
    try {
      const blob = await downloadReport(report.id, user_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = report.report_filename || `report_${report.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setNotice({ type: 'error', text: err.message || 'Download failed.' });
      setTimeout(() => setNotice(null), 4000);
    }
  };

  const vitalList = detail && detail.all_vital_results
    ? Object.entries(detail.all_vital_results)
        .filter(([k, v]) => !META_KEYS.includes(k) && v !== null && v !== '' && v !== undefined)
        .map(([k, v]) => ({ key: k, label: k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), value: v }))
    : [];

  const formatDate = (iso) => {
    if (!iso) return 'N/A';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? 'N/A' : d.toLocaleString(undefined, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="rpg-container">
      <div className="rpg-header">
        <div className="rpg-title-wrap">
          <div className="rpg-title-icon"><FileText size={20} /></div>
          <div>
            <h2 className="rpg-title">Reports</h2>
            <p className="rpg-sub">View and download your generated vital sign reports.</p>
          </div>
        </div>
        <button className="rpg-refresh" onClick={handleRefresh} title="Refresh">
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rpg-error">
          <Info size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="rpg-toolbar">
        <div className="rpg-search">
          <Search size={15} className="rpg-search-icon" />
          <input
            placeholder="Search by patient name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="rpg-loading">
          <div className="rpg-spinner" />
          <p>Loading reports...</p>
        </div>
      ) : (
        <ReportTable
          reports={reports}
          onView={handleView}
          onDownload={handleDownload}
          sort={sort}
          onSortChange={setSort}
        />
      )}

      {notice && (
        <div className={`rpg-notice ${notice.type === 'error' ? 'rpg-notice-error' : ''}`}>
          <Info size={15} />
          <span>{notice.text}</span>
        </div>
      )}

      {selected && (
        <div className="rpg-overlay" onClick={() => setSelected(null)}>
          <div className="rpg-detail" onClick={(e) => e.stopPropagation()}>
            <button className="rpg-detail-close" onClick={() => setSelected(null)} aria-label="Close">
              <X size={18} />
            </button>
            <div className="rpg-detail-header">
              <div className="rpg-detail-icon"><FileText size={26} /></div>
              <div>
                <h3 className="rpg-detail-name">{detail ? detail.patient_name : selected.patient_name}</h3>
                <p className="rpg-detail-meta">
                  Report #{selected.id} &middot; {formatDate(selected.created_at)}
                </p>
              </div>
              <button
                className="rpg-detail-download"
                onClick={() => handleDownload(selected)}
              >
                <Download size={15} />
                Download PDF
              </button>
            </div>

            {detailLoading && (
              <div className="rpg-detail-loading">
                <div className="rpg-spinner" />
                <p>Loading details...</p>
              </div>
            )}
            {detailError && (
              <div className="rpg-error"><Info size={16} /><span>{detailError}</span></div>
            )}

            {detail && !detailLoading && (
              <>
                <div className="rpg-detail-info">
                  <div className="rpg-info-item">
                    <User size={15} />
                    <div>
                      <p className="rpg-info-label">Patient Name</p>
                      <p className="rpg-info-val">{detail.patient_name || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="rpg-info-item">
                    <Hash size={15} />
                    <div>
                      <p className="rpg-info-label">Age</p>
                      <p className="rpg-info-val">{detail.age ?? 'N/A'}</p>
                    </div>
                  </div>
                  <div className="rpg-info-item">
                    <Calendar size={15} />
                    <div>
                      <p className="rpg-info-label">Scan Date &amp; Time</p>
                      <p className="rpg-info-val">{formatDate(detail.scan_datetime)}</p>
                    </div>
                  </div>
                </div>

                <div className="rpg-detail-section">
                  <h4 className="rpg-detail-section-title">Vital Measurements</h4>
                  {vitalList.length ? (
                    <div className="rpg-vitals-grid">
                      {vitalList.map((item) => (
                        <div key={item.key} className="rpg-vital-item">
                          <p className="rpg-vital-label">{item.label}</p>
                          <p className="rpg-vital-value">{String(item.value)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="rpg-no-vitals">No vital measurements stored for this report.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
